/* eslint-disable no-magic-numbers */

import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from "react-query";

import { apiCollections, apiCreators, apiNiches, apiTags, apiMain, apiVisitor } from "services";
import { isAuthenticated } from "services/Auth";

import {
  ICollection,
  IFollowingListResponse,
  IGallery,
  IGifInfo,
  IGifList,
  INiche,
  INicheList,
  IShortUserList,
  ITag,
  ITagList,
  IUser,
  IUserSearchResults,
} from "services/types";
import { IUserGifsParams } from "./services/RedGifsService";
import { IGetMatchedTagsParams } from "./services/TagsService";

const TRENDING_TAGS_KEY = "trending-tags";
const TRENDING_CREATORS_KEY = "trending-creators";
const TRENDING_NICHES_KEY = "trending-niches";
const SINGLE_NICHE_KEY = "single-niche";

const DEFAULT_OPTIONS = {
  refetchOnWindowFocus: false,
  refetchOnMount: false,
  retry: false,
};

export const useFollowingIdsQuery = () => {
  return useQuery(["following-ids"], async (): Promise<string[]> => {
    const auth = await isAuthenticated();

    if (auth) {
      return await apiMain.getFollowingIds();
    } else {
      return [];
    }
  }, DEFAULT_OPTIONS);
};

export const useFollowingQuery = (props: {
  cursor?: string;
}) => {
  return useQuery(["following"], async (): Promise<IFollowingListResponse|undefined> => {
    const auth = await isAuthenticated();

    if (auth) {
      return await apiMain.fetchUserFollows({
        count: 100,
      });
    } else {
      return undefined;
    }
  }, DEFAULT_OPTIONS);
};

/**
 * Returns a list of gif ids that the user liked previously.
 */
export const useLikesQuery = () => {
  return useQuery(["likes"], async (): Promise<string[]> => {
    try {
      const auth = await isAuthenticated();

      if (auth) {
        console.debug("The user is authenticated, requesting likes.");
        return await apiMain.getLikedGifs();
      } else {
        console.debug("The user is not authenticated, not requesting likes.");
        return [];
      }
    } catch (e) {
      console.error("Error checking for user auth.", e);
      return [];
    }
  }, DEFAULT_OPTIONS);
};

export const useTrendingTags = () => {
  return useQuery([TRENDING_TAGS_KEY], async (): Promise<ITag[]> => {
    const res = await apiTags.getTrendingTags();
    return res;
  }, DEFAULT_OPTIONS);
};

export const useTrendingCreators = () => {
  return useQuery([TRENDING_CREATORS_KEY], async (): Promise<IUser[]> => {
    return (await apiCreators.getTrendingCreators()).items;
  }, DEFAULT_OPTIONS);
};

export const useTrendingNiches = () => {
  return useQuery([TRENDING_NICHES_KEY], async (): Promise<INiche[]> => {
    const res = await apiNiches.getTrendingNiches();
    return res.niches;
  }, DEFAULT_OPTIONS);
};

export const useSuggestedNiches = () => {
  return useQuery(["personal-suggested-niches"], async (): Promise<INiche[]> => {
    const auth = await isAuthenticated();

    if (auth) {
      return await apiNiches.getSuggestedNiches();
    } else {
      return [];
    }
  }, DEFAULT_OPTIONS);
};

export const useSingleNiche = (id?: string) => {
  return useQuery([SINGLE_NICHE_KEY, id], async (): Promise<INiche|undefined> => {
    if (id === undefined) {
      return undefined;
    } else {
      return await apiNiches.getNiche(id);
    }
  }, DEFAULT_OPTIONS);
};

export const useUserInfo = (id: string|undefined) => {
  return useQuery(["user-info", id], async (): Promise<IUser> => {
    if (id === undefined) {
      throw Error("user id not specified");
    }

    return await apiMain.fetchCreatorById(id);
  }, DEFAULT_OPTIONS);
};

export const useMyNichesQuery = (props: {
  previews: boolean;
}) => {
  return useQuery(
    ["my-niches", props],

    async (): Promise<INicheList> => {
      const auth = await isAuthenticated();

      if (auth) {
        console.debug("The user is authenticated, requesting my niches.");
        return await apiNiches.getMyNiches({
          previews: props.previews,
        });
      } else {
        console.debug("The user is not authenticated, not requesting my niches.");
        throw new Error("need to be logged in to read own niches");
      }
    },

    DEFAULT_OPTIONS,
  );
};

export const useNewVerifiedCreators = () => {
  return useQuery(
    ["new-verified-creators"],

    async (): Promise<IShortUserList> => {
      return await apiCreators.getNewVerifiedCreators();
    },

    DEFAULT_OPTIONS,
  );
};

interface INicheGifsQuery {
  id: string | undefined;
  page: number;
  type: string | undefined;
  order: string;
  count: number;
}

export const useNicheGifs = (params: INicheGifsQuery) => {
  return useQuery(
    ["niche-gifs", params],

    async ({ queryKey }): Promise<IGifList|undefined> => {
      if (params.id === undefined) {
        return undefined;
      }

      return await apiNiches.getNicheGifs({
        id: params.id,
        page: params.page,
        type: params.type,
        order: params.order,
        count: params.count,
      });
    },

    DEFAULT_OPTIONS,
  );
};

export const useSearchCreatorsQuery = (params: {
    order: string;
    page: number;
    tags?: string;
    verified: boolean;
}) => {
  return useQuery(
    ["search-creators", params],

    async ({ queryKey }): Promise<IUserSearchResults> => {
      return await apiCreators.searchCreators({
        order: params.order,
        page: params.page,
        tags: params.tags,
        verified: params.verified,
      });
    },

    DEFAULT_OPTIONS,
  );
};

export const useSearchGifsQuery = (params: {
  tag?: string;
  order: string;
  count: number;
  type?: string;
  page?: number;
  following?: boolean;
  favourites?: boolean;
  verified?: boolean;
}) => {
  return useQuery(
    ["search-gifs", params],

    async ({ queryKey }): Promise<IGifList> => {
      return await apiMain.searchGifs({
        search_text: params.tag,
        order: params.order,
        count: params.count,
        type: params.type,
        page: params.page,
        following: !!params.following,
        favourites: !!params.favourites,
        verified: !!params.verified,
      });
    },

    DEFAULT_OPTIONS,
  );
};

interface ISearchUserGifsQuery {
  username: string;
  order: string;
  page: number;
  count?: number;
  type?: string;
  tags?: string;
}

export const useSearchUserGifs = (params: ISearchUserGifsQuery) => {
  return useQuery(
    ["search-user-gifs", params],

    async (): Promise<IGifList> => {
      return await apiMain.searchUserGifs({
        username: params.username,
        order: params.order,
        count: params.count ?? 10,
        type: params.type,
        page: params.page,
        tags: params.tags
      });
    },

    DEFAULT_OPTIONS,
  );
};

interface ISearchMyGifsQuery {
  order: string;
  page: number;
  type?: string;
  tags?: string;
}

export const useSearchMyGifs = (params: ISearchMyGifsQuery) => {
  return useQuery(
    ["search-my-gifs", params],

    async (): Promise<IGifList> => {
      return await apiMain.searchMyGifs({
        order: params.order,
        count: 50,
        type: params.type,
        page: params.page,
        tags: params.tags
      });
    },

    DEFAULT_OPTIONS,
  );
};

export const useGifQuery = (id: string) => {
  return useQuery(
    ["single-gif-info", id],

    async (): Promise<IGifInfo> => {
      return await apiMain.fetchGifById(id);
    },

    DEFAULT_OPTIONS,
  );
};

export const useCollectionItemsQuery = (props: {
  user: string;
  collection: string;
  page: number;
  count: number;
  showPrivate?: boolean;
}) => {
  return useQuery(
    ["collection-items", props],

    async (): Promise<IGifList> => {
      if (props.showPrivate) {
        return await apiCollections.getMyCollectionItems({
          collectionId: props.collection,
          page: props.page,
          count: props.count
        });
      } else {
        return await apiCollections.getCollectionItems({
          userId: props.user,
          collectionId: props.collection,
          page: props.page,
          count: props.count
        });
      }
    },

    DEFAULT_OPTIONS,
  );
};

export const useAccountQuery = (props: {
  userName: string;
}) => {
  return useQuery(
    ["account-info", props],

    async (): Promise<IUser> => {
      return await apiMain.getUser({
        userName: props.userName,
      });
    },

    DEFAULT_OPTIONS,
  );
};

export const useVerifyUserProfileMutation = () => {
  return useMutation((verifyData: FormData) => {
    return apiVisitor.verifyUserProfile(verifyData);
  });
};

export const useCollectionQuery = (props: {
  userName: string;
  collectionId: string;
  showPrivate: boolean;
}) => {
  return useQuery(
    ["collection-info", props],

    async (): Promise<ICollection> => {
      if (props.showPrivate) {
        return await apiCollections.getMyCollection({
          collectionId: props.collectionId,
        });
      } else {
        return await apiCollections.getCollectionById({
          userName: props.userName,
          collectionId: props.collectionId,
        });
      }
    },

    DEFAULT_OPTIONS,
  );
};

export const useMeQuery = () => {
  return useQuery(
    ["get-me"],

    async (): Promise<IUser|undefined> => {
      const auth = await isAuthenticated();

      if (auth) {
        return await apiMain.getMe();
      } else {
        return undefined;
      }
    },

    DEFAULT_OPTIONS,
  );
};

export const useNichesQuery = (props: {
  order: string;
  previews?: boolean;
}) => {
  return useQuery(
    ["all-niches", props],

    async (): Promise<INicheList> => {
      return await apiNiches.getNiches({
        order: props.order,
        previews: props.previews === undefined ? true : props.previews,
      });
    },

    DEFAULT_OPTIONS,
  );
};

export const useTopGifsQuery = () => {
  return useQuery(
    ["top-gifs"],

    async (): Promise<IGifList> => {
      return await apiMain.searchGifs({
        order: "top7",
        count: 10,
      });
    },

    DEFAULT_OPTIONS,
  );
};

export const useTrendingGifsQuery = () => {
  return useQuery(
    ["trending-gifs"],

    async (): Promise<IGifList> => {
      return await apiMain.searchGifs({
        order: "trending",
        type: "g",
        count: 10,
      });
    },

    DEFAULT_OPTIONS,
  );
};

export const useTrendingImagesQuery = () => {
  return useQuery(
    ["trending-images"],

    async (): Promise<IGifList> => {
      return await apiMain.searchGifs({
        order: "trending",
        type: "i",
        count: 10,
      });
    },

    DEFAULT_OPTIONS,
  );
};

export const useGalleryQuery = (id: string|null|undefined) => {
  return useQuery(
    ["gallery", id],

    async (): Promise<IGallery|undefined> => {
      if (id) {
        console.debug(`Requesting contents of gallery ${id}`);
        return await apiMain.getGallery(id);
      } else {
        return undefined;
      }
    },

    DEFAULT_OPTIONS,
  );
};

export const useCreatorTagsQuery = (id: string) => {
  return useQuery(
    ["creators-tags", id],

    async () => {
      return await apiMain.fetchCreatorTagsById(id);
    },

    DEFAULT_OPTIONS,
  );
};

export const useTagsQuery = () => {
  return useQuery(
    ["tags"],

    async (): Promise<ITagList> => {
      return await apiTags.getTags();
    },

    DEFAULT_OPTIONS,
  );
};

export const useMeGifsQuery = (props: IUserGifsParams) => {
  return useQuery(
    ["meGifs", ...Object.values(props)],

    () => apiMain.fetchUserGifs(props),

    DEFAULT_OPTIONS,
  );
};

export const useDeleteGifsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (ids: string[]) => {
      const requests = ids.map(id => apiMain.deleteGif(id));
      return await Promise.all(requests);
    },
    {
      onSuccess: () => {
        return queryClient.invalidateQueries({ queryKey: ["meGifs"] });
      },
    }
  );
};

export const useChangeGifsPrivacyMutation = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ ids, value }: { ids: string[]; value: 0 | 1 }) => {
      const requests = ids.map(id => apiMain.changeGifPrivacy({ gifId: id, value }));
      return await Promise.all(requests);
    },
    {
      onSuccess: () => {
        return queryClient.invalidateQueries({ queryKey: ["meGifs"] });
      },
    }
  );
};

export const useChangeGifTagsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ ids, tags }: { ids: string[]; tags: string[] }) => {
      const requests = ids.map(id => apiMain.changeGifTags(id, tags));
      return await Promise.all(requests);
    },
    {
      onSuccess: () => {
        return queryClient.invalidateQueries({ queryKey: ["meGifs"] });
      },
    }
  );
};

/**
 * Perform a number of different requests for the related gifs,
 * depending on where the user comes from.
 */
export const useSmartRelatedGifsQuery = (props: {
  gif: string;
  rel: string;
  page: number;
  verified: boolean;
  order: string | undefined;
}) => {
  return useQuery(
    ["related-gifs-smart", props.gif, props.rel, props.page],

    async (): Promise<IGifList> => {
      const m1 = props.rel.match(/^niche:(.+)/);

      if (m1 !== null) {
        console.debug(`Smart feed source: niche ${m1[1]} page=${props.page}.`);

        return await apiNiches.getNicheGifs({
          id: m1[1],
          page: props.page,
          count: 100,
          type: undefined,
          order: props.order || "trending",
        });
      }

      const m2 = props.rel.match(/^tag:([^,]+),([agi])/);

      if (m2 !== null) {
        console.debug(`Smart feed source: tag ${m2[1]}, mode=${m2[2]} page=${props.page}.`);

        return await apiMain.searchGifs({
          search_text: m2[1],
          page: props.page,
          count: 100,
          order: props.order || "trending",
          verified: props.verified,
          type: m2[2] === "a" ? undefined : m2[2],
        });
      }

      const m3 = props.rel.match(/^user:(.+)/);

      if (m3 !== null) {
        console.debug(`Smart feed source: user ${m3[1]} page=${props.page}.`);

        const auth = await isAuthenticated();
        const itsMe = auth ? (await apiMain.getMe()).username === m3[1] : false;
        const options = {
          order: props.order || "trending",
          count: 100,
          type: undefined,
          page: props.page
        };

        return itsMe
          ? await apiMain.searchMyGifs(options)
          : await apiMain.searchUserGifs({ username: m3[1], ...options });
      }

      const m4 = props.rel.match(/^c:(.+)/);

      if (m4 !== null) {
        const splitedRel = props.rel.split(":");

        const auth = await isAuthenticated();
        let itsMe = false;

        if (auth) {
          const user = await apiMain.getMe();
          if (user.username === splitedRel[1]) {
            itsMe = true;
          }
        }

        console.debug(`Smart feed source: collection ${m4[1]} page=${props.page}.`);

        if (itsMe) {
          return await apiCollections.getMyCollectionItems({
            collectionId: splitedRel[2],
            page: props.page,
            count: 100
          });
        } else {
          return await apiCollections.getCollectionItems({
            userId: splitedRel[1],
            collectionId: splitedRel[2],
            page: props.page,
            count: 100
          });
        }
      }

      console.debug(`Smart feed source: default, rel=${props.rel} page=${props.page}`);

      return await apiMain.getRelatedGifs({
        id: props.gif,
        page: props.page,
      });
    },

    DEFAULT_OPTIONS,
  );
};

export const useForYouFeedQuery = (props: {
  page: number;
  count: number;
}) => {
  return useQuery(
    ["for-you-feed", props],

    () => apiMain.getForYouFeed({
      page: props.page,
      count: props.count,
    }),

    DEFAULT_OPTIONS,
  );
};

export const useHomeFeedQuery = (props: {
  page: number;
  count: number;
}) => {
  return useQuery(
    ["home-feed-default", props],

    () => apiMain.getHomeFeed({
      page: props.page,
      count: props.count,
    }),

    DEFAULT_OPTIONS,
  );
};

export const useLikedFeedQuery = (props: {
  page: number;
  count: number;
  type?: string;
}) => {
  return useQuery(
    ["liked-feed", props],

    () => apiMain.getLikedFeed({
      page: props.page,
      count: props.count,
      type: props.type,
    }),

    DEFAULT_OPTIONS,
  );
};

export const useMatchedTagsQuery = (props: IGetMatchedTagsParams) => {
  return useQuery(
    ["matchedTags", props],

    () => {
      return apiTags.getMatchedTags(props);
    },

    DEFAULT_OPTIONS,
  );
};

export const useFollowingFeed = () => {
  const fetch = async ({
    pageParam = 1,
  }): Promise<IUserSearchResults> => {
    return await apiMain.getFollowing({
      count: 100,
      page: pageParam as number,
    });
  };

  return useInfiniteQuery("following-feed", fetch, {
    getNextPageParam: (lastPage: IUserSearchResults, pages) => {
      if (lastPage.page < lastPage.pages) {
        return lastPage.page + 1;
      } else {
        return null;
      }
    },
    ...DEFAULT_OPTIONS,
  });
};

export const useFollowersFeed = () => {
  const fetch = async ({
    pageParam = 1,
  }): Promise<IUserSearchResults> => {
    return await apiMain.getFollowers({
      count: 100,
      page: pageParam,
    });
  };

  return useInfiniteQuery("followers-feed", fetch, {
    getNextPageParam: (lastPage: IUserSearchResults, pages) => {
      if (lastPage.page < lastPage.pages) {
        return lastPage.page + 1;
      } else {
        return null;
      }
    },
    ...DEFAULT_OPTIONS,
  });
};
